import type { FilterParams } from "epsm-api-types";
import { createChart, LineSeries, type Time } from "lightweight-charts";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/Spinner";
import type { PriceInfo } from "@/components/widgets/Chart/services/PriceService/PriceService";
import { UseData as UseDataPrice } from "@/components/widgets/Chart/services/UseData";
import type { Options } from "@/components/widgets/Revision/lib/services/UseData";
import { concatData } from "@/hooks/useGetData";
import { VertLine } from "@/lib/plugins/VertLine/VertLine";
import { CHART_COLORS } from "@/shared/constants/common";
import { COMPONENT_CONTAINER_CLASSES } from "../../../config";
import {
	getChartOptions,
	getColorByValue,
	getDateString,
} from "../../../lib/helpers";
import { RevisionsScoreChartDataSource } from "../../../types";
import { ChartSettingsPanel } from "../ChartSettingsPanel";
import { OPTIONS, OPTIONS_HOVER, SCORE_SERIES_TITLE } from "./config";
import { mapData } from "./lib/helpers";
import type { PriceScaleId, ScoreChartProps, SeriesData } from "./types";

export const ScoreChart = ({
	ticker,
	data,
	quarterlyReports,
	revisionsScoreChartDataSource,
	onStateChange,
}: ScoreChartProps) => {
	const [option, setOption] = useState<RevisionsScoreChartDataSource>(
		revisionsScoreChartDataSource,
	);
	const [dataPrice, setDataPrice] = useState<PriceInfo[]>([]);
	const [selectedSeries, setSeriesChange] =
		useState<string[]>(SCORE_SERIES_TITLE);

	const {
		execute: executePrice,
		isLoading: isLoadingPrice,
		error: errorPrice,
	} = UseDataPrice.create().useGetData();

	const chartContainerRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<any>(null);

	useEffect(() => {
		const getData = async () => {
			const rssDate = data.length ? data[0].date : undefined;
			const filter: FilterParams = [
				{ name: "ticker", condition: "eq", value: ticker },
			];
			if (rssDate) {
				filter.push({
					name: "date",
					condition: "gte",
					value: rssDate.getTime(),
				});
			}

			const prices = await concatData<PriceInfo, Options>(executePrice, {
				params: {
					sort: [{ name: "date", order: 1 }],
					fields: ["date", "adj_close"],
					filter: filter,
				},
			});
			setDataPrice(prices);
		};
		getData();
	}, [ticker, data, executePrice]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <We don't need to react on function changes>
	useEffect(() => {
		if (!chartContainerRef.current) return;

		const handleResize = () => {
			if (chartContainerRef.current?.clientWidth) {
				chart.applyOptions({
					width: chartContainerRef.current.clientWidth,
					height: chartContainerRef.current.clientHeight,
				});
			}
		};

		const chart = createChart(
			chartContainerRef.current,
			getChartOptions(chartContainerRef, true),
		);
		chartRef.current = chart;

		const addSeries = (
			data: SeriesData[],
			seriesName: string,
			priceScaleId: PriceScaleId = "right",
		) => {
			const series = chart.addSeries(LineSeries, {
				lineWidth: 1,
				color: getColorByValue(seriesName, SCORE_SERIES_TITLE),
				priceScaleId,
			});
			series.applyOptions({ lastValueVisible: false, priceLineVisible: false });
			series.setData(data);

			if (data.length && quarterlyReports.length) {
				const startDate = new Date(data[0].time);
				const filteredQuarterlyReports = quarterlyReports.filter(
					(item) => !!item.earning_date && item.earning_date >= startDate,
				);

				const emTimes = filteredQuarterlyReports.map((item) =>
					getDateString(item.earning_date!),
				);
				const vertLines = emTimes.map(
					(emTime) =>
						new VertLine(chart, series, emTime as Time, {
							showLabel: false,
							color: CHART_COLORS.GRAY,
						}),
				);
				for (const vertLine of vertLines) {
					series.attachPrimitive(vertLine);
				}
			}
		};

		let startTime: string | undefined;

		const seriesMap = [
			{
				key: `rs.${option}`,
				title: SCORE_SERIES_TITLE[1],
				data,
				priceScaleId: "left" as PriceScaleId,
			},
			{
				key: `rss.${option}`,
				title: SCORE_SERIES_TITLE[2],
				data,
				priceScaleId: "left" as PriceScaleId,
			},
			{ key: "close", title: SCORE_SERIES_TITLE[0], data: dataPrice },
		];

		for (const { key, title, data, priceScaleId } of seriesMap) {
			if (selectedSeries.includes(title)) {
				const seriesData = mapData(data, key, startTime);

				if (
					option === RevisionsScoreChartDataSource.FINNHUB &&
					key.startsWith("rs") &&
					seriesData.length
				) {
					startTime = seriesData[0].time;
				}

				addSeries(seriesData, title, priceScaleId);
			}
		}

		chart.timeScale().fitContent();

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);

			chart.remove();
		};
	}, [
		data,
		dataPrice,
		option,
		selectedSeries,
		chartContainerRef?.current?.clientWidth,
		chartContainerRef?.current?.clientHeight,
		quarterlyReports,
	]);

	const handleSwitch = () => {
		const next =
			option === RevisionsScoreChartDataSource.ZACKS
				? RevisionsScoreChartDataSource.FINNHUB
				: RevisionsScoreChartDataSource.ZACKS;
		setOption(next);
		onStateChange?.({ revisionsScoreChartDataSource: next });
	};

	if (!data.length && !dataPrice.length) return false;

	return (
		<div className={COMPONENT_CONTAINER_CLASSES}>
			<h3 className="text-light-gray text-lg w-full ">
				Revisions Score Chart: EPS/ Sales
			</h3>
			<ChartSettingsPanel
				switchLabel={OPTIONS[option]}
				hoverLabel={OPTIONS_HOVER[option]}
				handleSwitch={handleSwitch}
				toggles={SCORE_SERIES_TITLE}
				selectedToggles={selectedSeries}
				setSelectedToggles={setSeriesChange}
			/>
			{isLoadingPrice ? (
				<Spinner />
			) : errorPrice ? (
				<div className="text-red-500">{`Failed to load revision data: ${errorPrice}`}</div>
			) : (
				<div
					className="aspect-[1.618]"
					ref={chartContainerRef}
					data-testid="chart-container"
				/>
			)}
		</div>
	);
};
